import { useEffect, useLayoutEffect, useRef } from "react";
import { toast } from "sonner";
import { CustomerTemplate } from "@/components/CustomerTemplateManager";
import { Item } from "@/types/purchase";
import * as parsedOrderService from "@/supabase/parsedorder/parsedOrderService";
import { readParsingOptions } from "@/hooks/useParsingOptions";
import { buildProductListFromSettlement } from "@/utils/shippingProductList";
import { logError } from "@/lib/sentry";

// 연속 항목 변경(추가/삭제/수량)을 한 번의 검사로 흡수하는 디바운스.
// 옵티미스틱 롤백(실패 시 원복)도 이 창 안에서 원상복구되어 오탐을 만들지 않는다.
const NUDGE_DEBOUNCE_MS = 1200;

// 감시 엔트리 수명 — 토스트 수명(8s)과 지연 롤백을 넉넉히 덮고, 무시된 토스트의 엔트리가
// 세션 내내 쌓이는 것을 막는다(매 커밋 walk 비용을 소수로 유지).
const WATCH_TTL_MS = 60_000;

const extractOrderId = (customerId: string): number | null => {
  const match = customerId.match(/^order_(\d+)$/);
  return match ? Number(match[1]) : null;
};

const nudgeToastId = (orderId: number): string => `shipping-stale-${orderId}`;

const resolveCustomer = (templates: CustomerTemplate[], orderId: number): CustomerTemplate | null =>
  templates.find((c) => c.id === `order_${orderId}`) ?? null;

// 훅 인스턴스당 1개 생성되는 가변 컨텍스트 — 모듈 레벨 async 함수들이 항상 최신 상태를 읽도록
// ref 묶음을 한 객체로 전달한다.
type NudgeCtx = {
  /** customerTemplates live 참조 (deps 없는 useLayoutEffect로 매 커밋 동기화) */
  templates: { current: CustomerTemplate[] };
  /** 주문별 디바운스 타이머 */
  timers: Map<number, ReturnType<typeof setTimeout>>;
  /**
   * 감시 대상 주문(토스트를 띄웠거나 반영 직후) → 마지막으로 본 "합성값(synth)"과 감시 시각.
   * 참조가 아닌 합성 문자열을 비교해 refetch(동일 값·새 참조) churn과 가격-만 변경을 무시하고,
   * 의미 있는 변화(롤백 포함)만 재검사한다.
   * WATCH_TTL_MS를 넘긴 엔트리는 매 커밋 walk에서 프루닝되어 무한 누적을 막는다.
   */
  watched: Map<number, { synth: string; watchedAtMs: number }>;
  /** 언마운트 후 in-flight async(finally 포함)가 타이머를 재예약하지 못하게 막는 플래그. */
  disposed: boolean;
};

const scheduleCheck = (ctx: NudgeCtx, orderId: number): void => {
  if (ctx.disposed) return;
  const existing = ctx.timers.get(orderId);
  if (existing) clearTimeout(existing);
  ctx.timers.set(
    orderId,
    setTimeout(() => {
      ctx.timers.delete(orderId);
      void checkAndNudge(ctx, orderId);
    }, NUDGE_DEBOUNCE_MS)
  );
};

export interface UseShippingSnapshotNudgeProps {
  user: { id: string } | null;
  activeCustomerId: string | null;
  activeCustomer: CustomerTemplate | null;
  customerTemplates: CustomerTemplate[];
  ordersWithShippingInfo: Set<number>;
}

/**
 * 배송정보(parsed_order) 상품명 스냅샷 stale 감지 토스트.
 *
 * 모든 로컬 item 변경은 Home의 handleItemsChangeAndPersist(단일 chokepoint)를 지나
 * activeCustomer.items "참조"를 바꾸므로, 이 훅은 저장 경로를 건드리지 않고
 * 참조 변화만 관찰한다(관찰 전용 — 실패해도 기존 동작 무영향).
 *
 * 트리거 조건(전부 만족 시 디바운스 검사 예약):
 *  1) 로그인 상태에서 같은 고객이 활성인 채로 items 참조가 변경됨 (고객 전환은 기준선만 갱신)
 *  2) 해당 주문에 배송정보가 이미 존재 (ordersWithShippingInfo)
 *  3) parseProductName 옵션 ON (이벤트 시점에 localStorage에서 직접 읽음)
 *  4) 합성값(스냅샷에 실제 영향을 주는 이름/수량/구성)이 실제로 달라짐 — refetch가 전체 배열을
 *     재구성해 참조만 바뀐 경우와 가격-만 변경(스냅샷 무관)을 걸러낸다.
 *
 * 정합성 불변식 2개 (codexloop R4 합의):
 *  - "쓰기는 검증으로 마감": applyProductName은 성공/실패와 무관하게 재검사를 예약한다.
 *    클릭~쓰기 사이에 롤백/편집이 끼어 낡은 값이 영속됐다면 재검사가 재-토스트로 자가 치유.
 *  - "감시 주문 재검증": 토스트를 띄운 주문은 watched에 등록되고, 매 커밋의 useLayoutEffect가
 *    그 주문들의 "합성값" 변화(비활성 롤백 포함)를 감지해 재검사를 예약한다. 동등 판정은 토스트만
 *    무효화하고 엔트리는 TTL(쓰기/토스트 시점 기준 60초)까지 유지된다.
 * 검사 결과가 "배송정보 없음/빈 합성값/고객 소멸"이면 토스트 dismiss + 감시 해제.
 * "이미 일치"는 토스트만 dismiss하고 감시 엔트리는 정착 창(TTL)까지 유지한다.
 * 검사/액션 모두 결정 시점에 templatesRef에서 재해석 — 낡은 스냅샷을 쓰지 않는다.
 */
export function useShippingSnapshotNudge({
  user,
  activeCustomerId,
  activeCustomer,
  customerTemplates,
  ordersWithShippingInfo,
}: UseShippingSnapshotNudgeProps): void {
  const ctxRef = useRef<NudgeCtx | null>(null);
  if (!ctxRef.current) {
    ctxRef.current = {
      templates: { current: customerTemplates },
      timers: new Map(),
      watched: new Map(),
      disposed: false,
    };
  }
  const ctx = ctxRef.current;

  // latest-ref 규율: deps 없는 useLayoutEffect로 매 커밋 동기화 (커밋~effect 사이 낡은 읽기 창 제거).
  // 같은 자리에서 감시 주문 재검증 — watched는 토스트가 뜬 소수 주문뿐이라 커밋당 O(watched).
  useLayoutEffect(() => {
    ctx.templates.current = customerTemplates;
    const nowMs = Date.now();
    ctx.watched.forEach((entry, orderId) => {
      if (nowMs - entry.watchedAtMs > WATCH_TTL_MS) {
        ctx.watched.delete(orderId);
        return;
      }
      const current = resolveCustomer(customerTemplates, orderId);
      const currentSynth = current ? buildProductListFromSettlement(current) : "";
      if (currentSynth !== entry.synth) {
        ctx.watched.set(orderId, { synth: currentSynth, watchedAtMs: entry.watchedAtMs });
        scheduleCheck(ctx, orderId);
      }
    });
  });

  const prevRef = useRef<{ customerId: string | null; items: Item[] | null }>({
    customerId: null,
    items: null,
  });

  useEffect(() => {
    const items = activeCustomer?.items ?? null;
    const prev = prevRef.current;
    prevRef.current = { customerId: activeCustomerId, items };

    if (!user || !activeCustomerId || !activeCustomer) return;
    // 고객 전환/최초 선택 — 기준선만 갱신하고 토스트하지 않는다.
    if (prev.customerId !== activeCustomerId) return;
    // items 참조 미변경 = 항목 변경 아님 (다른 이유의 재렌더/refetch).
    if (prev.items === items) return;
    // 기준선이 로드 전(null)이면 편집 신호가 아니다 — 최초 로드에 토스트 금지.
    if (prev.items === null) return;

    const orderId = extractOrderId(activeCustomerId);
    if (!orderId || !ordersWithShippingInfo.has(orderId)) return;
    if (!readParsingOptions().parseProductName) return;

    // 참조는 refetch(전체 배열 재구성)로도 바뀔 수 있으므로, 스냅샷에 실제로 영향 주는
    // 변경(이름/수량/구성)만 합성값 동등 비교로 걸러낸다. 가격-만 변경도 스냅샷 무관이라 제외됨.
    // 합성 규칙 단일 소스(buildProductListFromSettlement)를 그대로 재사용 — 별도 비교 로직 복제 금지.
    const prevSynthesized = buildProductListFromSettlement({ ...activeCustomer, items: prev.items });
    const currSynthesized = buildProductListFromSettlement(activeCustomer);
    if (prevSynthesized === currSynthesized) return;

    scheduleCheck(ctx, orderId);
    // ctx는 인스턴스 수명 동안 단일 객체(안정 참조)라 deps에 넣어도 재발화 원인이 되지 않는다.
  }, [user, activeCustomerId, activeCustomer, ordersWithShippingInfo, ctx]);

  // 언마운트 시 대기/재예약 차단 — in-flight async(finally 재검사 포함)가 타이머를 부활시키지 않도록
  // disposed를 세운다. StrictMode(dev)의 mount→cleanup→re-mount에서 같은 ctx가 재사용되므로
  // effect 본문에서 반드시 되살린다.
  useEffect(() => {
    ctx.disposed = false;
    return () => {
      ctx.disposed = true;
      // 전역 Toaster는 라우트 전환을 넘어 살아남으므로, 감시 중이던 토스트를 함께 무효화 —
      // dispose 후 frozen 상태로 클릭 가능한 낡은 액션을 남기지 않는다.
      ctx.watched.forEach((_entry, orderId) => toast.dismiss(nudgeToastId(orderId)));
      ctx.timers.forEach((t) => clearTimeout(t));
      ctx.timers.clear();
      ctx.watched.clear();
    };
  }, [ctx]);
}

async function checkAndNudge(ctx: NudgeCtx, orderId: number): Promise<void> {
  try {
    if (ctx.disposed) return;
    // 사전 가드(fetch 비용 절약): 이미 빈 합성값/고객 소멸이면 조회 없이 무효화.
    const pre = resolveCustomer(ctx.templates.current, orderId);
    if (!pre || !buildProductListFromSettlement(pre)) {
      toast.dismiss(nudgeToastId(orderId));
      ctx.watched.delete(orderId);
      return;
    }

    const stored = await parsedOrderService.getShippingInfoByOrderId(orderId);

    if (ctx.disposed) return; // 언마운트 후 토스트 부활 금지
    // await "후" 재해석 — 조회 중 undo/롤백이 있었으면 낡은 값으로 토스트하지 않는다.
    // 늦게 도착한 검사도 이 지점에서 최신 상태를 읽으므로 순서에 무관하게 동일 판정.
    const customer = resolveCustomer(ctx.templates.current, orderId);
    const synthesized = customer ? buildProductListFromSettlement(customer) : "";
    // 빈 합성값/고객 소멸/배송정보 무행 — 반영(쓰기)이 불가능한 상태이므로 감시도 종료.
    if (!customer || !synthesized || !stored) {
      toast.dismiss(nudgeToastId(orderId));
      ctx.watched.delete(orderId);
      return;
    }
    // 이미 일치: 토스트는 무효화하되 감시 엔트리는 TTL까지 유지(정착 창) — 쓰기 검증이
    // 통과한 "직후" 도착하는 지연 롤백(codexloop R6)을 놓치지 않기 위해 즉시 해제하지 않는다.
    if ((stored.productName ?? "") === synthesized) {
      toast.dismiss(nudgeToastId(orderId));
      const existing = ctx.watched.get(orderId);
      if (existing) ctx.watched.set(orderId, { synth: synthesized, watchedAtMs: existing.watchedAtMs });
      return;
    }

    // 토스트를 띄우는 순간부터 감시 대상 — 비활성 롤백 등 이후의 items 변화가 재검사를 만든다.
    ctx.watched.set(orderId, { synth: synthesized, watchedAtMs: Date.now() });
    toast(`'${customer.nickname}'님의 변경사항이 배송정보 상품명에 아직 반영되지 않았어요.`, {
      id: nudgeToastId(orderId),
      duration: 8000,
      action: {
        label: "배송정보에 반영",
        onClick: () => {
          void applyProductName(ctx, orderId);
        },
      },
    });
  } catch (error) {
    // 관찰 전용 경로 — 실패해도 기존 동작에 영향 없음. 로깅만.
    logError(error as Error, { context: "배송정보 상품명 stale 검사 실패" });
  }
}

async function applyProductName(ctx: NudgeCtx, orderId: number): Promise<void> {
  try {
    if (ctx.disposed) return; // dispose 후 클릭(경합 창) — frozen 템플릿으로 쓰지 않는다
    // 클릭 "시점"의 items로 재합성 — 토스트 노출 중 추가 편집/undo/롤백이 있어도 낡은 값을 쓰지 않는다.
    const customer = resolveCustomer(ctx.templates.current, orderId);
    const productName = customer ? buildProductListFromSettlement(customer) : "";
    if (!customer || !productName) {
      toast.error("반영할 상품명이 없습니다.");
      return;
    }
    await parsedOrderService.updateProductNameByOrderId(orderId, productName);
    if (!ctx.disposed) {
      toast.success(`'${customer.nickname}'님의 배송정보 상품명을 최신 주문으로 갱신했어요.`);
    }
  } catch (error) {
    logError(error as Error, { context: "배송정보 상품명 반영 실패" });
    if (!ctx.disposed) {
      toast.error("배송정보 상품명 반영에 실패했습니다.");
    }
  } finally {
    // "쓰기는 검증으로 마감" 불변식: 성공/실패와 무관하게 재검사 예약.
    // 클릭 캡처~patch 완료 사이에 롤백/편집이 끼어 낡은 값이 영속된 경우(codexloop R4 시퀀스),
    // 이 검증 검사가 stored ≠ 현재 합성값을 발견해 재-토스트로 자가 치유한다.
    // 쓰기 직후는 정착 창의 기준점 — 감시 데드라인을 갱신해 TTL이 쓰기 이후 60초를 보장하게 한다.
    const entry = ctx.watched.get(orderId);
    if (entry) ctx.watched.set(orderId, { synth: entry.synth, watchedAtMs: Date.now() });
    scheduleCheck(ctx, orderId);
  }
}
